import { DailyServiceMetrics } from '@/types/service/serviceHealth';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import { fillMissingDaysProtected, getStatusColor } from '@/utils/statusPageHelpers';

interface DevOpsUptimeChartProps {
  metrics: DailyServiceMetrics[];
}

const DevOpsUptimeChart = ({ metrics }: DevOpsUptimeChartProps) => {
  const filledMetrics = fillMissingDaysProtected(metrics);

  return (
    <div className='flex flex-col gap-2'>
      <div className='flex items-center justify-between text-xs text-muted-foreground'>
        <span>90 days ago</span>
        <span>Today</span>
      </div>
      <div className='flex items-end gap-[2px] h-12'>
        <TooltipProvider delayDuration={100}>
          {filledMetrics.map((metric, index) => {
            const isNoData = metric === null;
            const colorClasses = isNoData
              ? { bg: 'bg-gray-200', text: 'text-gray-700' }
              : getStatusColor(metric.status);

            return (
              <Tooltip key={index}>
                <TooltipTrigger asChild>
                  <div
                    className={`flex-1 ${colorClasses.bg} rounded-md hover:opacity-80 transition-opacity cursor-pointer h-full`}
                  />
                </TooltipTrigger>
                <TooltipContent side='top' className='max-w-xs'>
                  {isNoData ? (
                    <div className='text-sm'>
                      <p className='font-semibold'>No data</p>
                      <p className='text-xs text-muted-foreground'>
                        Date: {filledMetrics[index]?.date || 'Unknown'}
                      </p>
                    </div>
                  ) : (
                    <div className='text-sm space-y-1'>
                      <p className='font-semibold'>{metric.date}</p>
                      <p>Uptime: {metric.uptime_percent.toFixed(2)}%</p>
                      {metric.avg_response_time_ms !== null && (
                        <p>Avg Response: {metric.avg_response_time_ms.toFixed(0)}ms</p>
                      )}
                      <p className='text-xs text-muted-foreground'>
                        {metric.successful_checks}/{metric.total_checks} checks successful
                      </p>
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </TooltipProvider>
      </div>
    </div>
  );
};

export default DevOpsUptimeChart;
